'use client';

import { cn } from '@bridebook/aura';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ComponentProps, createContext, PropsWithChildren, useContext } from 'react';
import {
  infoBubbleArrowVariants,
  InfoBubbleContent,
  InfoBubbleHeader,
  InfoBubbleText,
  InfoBubbleVariant,
} from './info-bubble';

export const TooltipProvider = ({
  delayDuration = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    data-slot="tooltip-provider"
    delayDuration={delayDuration}
    {...props}
  />
);

export type TooltipVariant = InfoBubbleVariant;

const TooltipContext = createContext<TooltipVariant>('dark');

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltipContext must be used within a TooltipProvider');
  }
  return context;
};

type TooltipProps = ComponentProps<typeof TooltipPrimitive.Root> & {
  variant?: TooltipVariant;
};

export const Tooltip = ({ variant = 'dark', ...props }: TooltipProps) => (
  <TooltipProvider>
    <TooltipContext.Provider value={variant}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipContext.Provider>
  </TooltipProvider>
);

export const TooltipTrigger = ({
  className,
  asChild,
  type = 'button',
  ...props
}: ComponentProps<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger
    data-slot="tooltip-trigger"
    className={cn(!asChild && 'cursor-default', className)}
    asChild={asChild}
    type={type}
    {...props}
  />
);

export const TooltipHeader = ({ children, ...props }: PropsWithChildren) => {
  const variant = useTooltipContext();

  return (
    <InfoBubbleHeader variant={variant} {...props}>
      {children}
    </InfoBubbleHeader>
  );
};

export const TooltipText = ({ children, ...props }: PropsWithChildren) => {
  const variant = useTooltipContext();
  return (
    <InfoBubbleText variant={variant} {...props}>
      {children}
    </InfoBubbleText>
  );
};

type TooltipContentProps = ComponentProps<typeof TooltipPrimitive.Content> & {
  children: React.ReactNode;
};

export const TooltipContent = ({
  className,
  sideOffset = 0,
  children,
  ...props
}: TooltipContentProps) => {
  const variant = useTooltipContext();
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // base
          'z-[99999] w-fit max-w-[300px] origin-(--radix-tooltip-content-transform-origin) rounded-lg shadow-lg',
          // animation
          'animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...props}>
        <>
          <InfoBubbleContent variant={variant}>{children}</InfoBubbleContent>
          <TooltipPrimitive.Arrow className={cn(infoBubbleArrowVariants({ variant }))} />
        </>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};
